/**
 * Stripe Payment Links Configuration
 * Maps products to Stripe Payment Links and pricing
 */

export interface StripeProductConfig {
  productId: string; // Matches Product.id from products.ts
  paymentLink: string; // Stripe Payment Link URL
  priceInCents: number; // Price in cents (e.g., 2999 = $29.99)
  stripePriceId?: string; // Optional: Stripe Price ID for reference
}

// Client-side price config — server-side canonical prices are in:
// api/create-checkout-session.ts and api/process-credit-order.ts
export const stripeConfig: StripeProductConfig[] = [
  {
    productId: 'black-dog-tag',
    paymentLink: import.meta.env.VITE_STRIPE_LINK_BLACK_DOG_TAG || '',
    priceInCents: 1000, // $10.00
  },
  {
    productId: 'black-card',
    paymentLink: import.meta.env.VITE_STRIPE_LINK_BLACK_CARD || '',
    priceInCents: 1000, // $10.00
  },
  {
    productId: 'black-wallet',
    paymentLink: import.meta.env.VITE_STRIPE_LINK_BLACK_WALLET || '',
    priceInCents: 2500, // $25.00
  },
];

/**
 * Get Stripe Payment Link for a product
 * @param productId - The product ID (e.g., 'black-dog-tag')
 * @returns Payment Link URL or null if not found/configured
 */
export function getPaymentLink(productId: string): string | null {
  const config = stripeConfig.find((c) => c.productId === productId);
  if (!config?.paymentLink) {
    console.error(`Missing Stripe Payment Link for product: ${productId}`);
    console.error(
      `Check environment variable: VITE_STRIPE_LINK_${productId.toUpperCase().replace(/-/g, '_')}`
    );
    return null;
  }
  return config.paymentLink;
}

/**
 * Get product price in cents
 * @param productId - The product ID (e.g., 'black-dog-tag')
 * @returns Price in cents or 0 if not found
 */
export function getProductPrice(productId: string): number {
  const config = stripeConfig.find((c) => c.productId === productId);
  return config?.priceInCents || 0;
}

/**
 * Get all configured product prices
 * @returns Map of productId to price in cents
 */
export function getAllProductPrices(): Map<string, number> {
  return new Map(stripeConfig.map((c) => [c.productId, c.priceInCents]));
}
